import React from 'react';
import {
    Alert,
    Button,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import { Can } from '../Can';
import { TimesheetStatus } from '../timesheet/Timesheet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleExclamation,
    faCloudArrowUp,
    faEdit,
    faHandPointLeft,
    faHandPointRight,
    faInfoCircle,
    faThumbsDown,
    faThumbsUp,
    faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import _ from 'lodash-es';
import { isTimesheetValid } from './CrewTimesheetValidator';
import { hasAnyAdjustmentData } from './CrewTimesheet';

export default class CrewTimesheetManagementRow extends React.Component {
    constructor(props) {
        super(props);

        this.state = { showModal: false, rejectionNotes: null };

        this.toggleModal = this.toggleModal.bind(this);
        this.onRejectionNotesChange = this.onRejectionNotesChange.bind(this);
    }

    toggleModal() {
        const { showModal } = this.state;

        this.setState({ showModal: !showModal });
    }

    onRejectionNotesChange(e) {
        this.setState({ rejectionNotes: e.target.value });
    }

    render() {
        const {
            loading,
            timesheet,
            originalTimesheet,
            onAcceptClick,
            onRejectClick,
            onEnableAdminEditMode,
            job,
            isReadOnly,
            isTimesheetApprover,
        } = this.props;

        const { showModal, rejectionNotes } = this.state;

        const isValid = isTimesheetValid(timesheet, job);

        const canBeEdited =
            timesheet.timesheetStatusId < TimesheetStatus.Exported;

        const hideRejectButton =
            hasAnyAdjustmentData(timesheet) && !isTimesheetApprover;

        if (
            loading ||
            !timesheet ||
            (!isTimesheetApprover &&
                timesheet.timesheetStatusId > TimesheetStatus.Rejected) ||
            (isTimesheetApprover &&
                timesheet.timesheetStatusId > TimesheetStatus.Approved)
        ) {
            return null;
        }

        const acceptDisabled =
            !isValid ||
            !canBeEdited ||
            (!isTimesheetApprover &&
                timesheet.timesheetStatusId >= TimesheetStatus.Validated) ||
            (isTimesheetApprover &&
                timesheet.timesheetStatusId >= TimesheetStatus.Approved);

        const adjustmentModeDisabled =
            !canBeEdited ||
            timesheet.timesheetStatusId > TimesheetStatus.Approved ||
            (!isTimesheetApprover &&
                timesheet.timesheetStatusId > TimesheetStatus.Validated);

        return (
            <Can I="accept" a="timesheet">
                {!_.isEqual(timesheet, originalTimesheet) && (
                    <Alert color="info" fade={false}>
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                        Changes have been made to the timesheet. Re-submit or
                        refresh to enable approving/rejecting.
                    </Alert>
                )}

                {_.isEqual(timesheet, originalTimesheet) && (
                    <>
                        <div className="d-flex mt-5">
                            <div className="col-4">
                                {hideRejectButton && (
                                    <Alert color="warning" fade={false}>
                                        <FontAwesomeIcon
                                            icon={faInfoCircle}
                                            className="mr-2"
                                        />
                                        Rejection disabled due to adjustments.
                                    </Alert>
                                )}

                                {!isTimesheetApprover && (
                                    <Button
                                        color="danger"
                                        size="lg"
                                        onClick={this.toggleModal}
                                        disabled={
                                            !canBeEdited ||
                                            !isValid ||
                                            timesheet.timesheetStatusId ==
                                                TimesheetStatus.Rejected
                                        }
                                        hidden={hideRejectButton}
                                    >
                                        <FontAwesomeIcon
                                            icon={faThumbsDown}
                                            className="mr-2"
                                        />
                                        Reject
                                    </Button>
                                )}

                                {isTimesheetApprover && (
                                    <Button
                                        color="danger"
                                        size="lg"
                                        onClick={onRejectClick}
                                        disabled={
                                            !canBeEdited ||
                                            !isValid ||
                                            timesheet.timesheetStatusId !=
                                                TimesheetStatus.Validated
                                        }
                                        hidden={hideRejectButton}
                                    >
                                        <FontAwesomeIcon
                                            icon={faHandPointLeft}
                                            className="mr-2"
                                        />
                                        Return for Adjustment
                                    </Button>
                                )}
                            </div>

                            {!canBeEdited && <div className="col-4"></div>}

                            {canBeEdited && (
                                <div className="col-4">
                                    {isReadOnly && (
                                        <Button
                                            color="warning"
                                            size="lg"
                                            outline
                                            disabled={adjustmentModeDisabled}
                                            onClick={onEnableAdminEditMode}
                                        >
                                            <FontAwesomeIcon
                                                icon={faEdit}
                                                className="mr-2"
                                            />{' '}
                                            Adjust This Timesheet
                                        </Button>
                                    )}

                                    {!isReadOnly && (
                                        <Alert fade={false} color="warning">
                                            <FontAwesomeIcon
                                                icon={faCircleExclamation}
                                                className="mr-2"
                                            />
                                            Adjustment Mode Active
                                        </Alert>
                                    )}
                                </div>
                            )}

                            <div className="col-4">
                                <Button
                                    color="success"
                                    size="lg"
                                    className="float-right"
                                    onClick={onAcceptClick}
                                    disabled={acceptDisabled}
                                >
                                    {!isTimesheetApprover && (
                                        <>
                                            <FontAwesomeIcon
                                                icon={faHandPointRight}
                                                className="mr-2"
                                            />
                                            Mark Validated
                                        </>
                                    )}

                                    {isTimesheetApprover && (
                                        <>
                                            <FontAwesomeIcon
                                                icon={faThumbsUp}
                                                className="mr-2"
                                            />
                                            Approve
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>

                        <Modal isOpen={showModal} fade={false} size="xl">
                            <ModalHeader toggle={this.toggleModal}>
                                Rejection Notes
                            </ModalHeader>

                            <ModalBody>
                                <div>
                                    Please explain why you are rejecting this
                                    timesheet. These notes should enable the
                                    foremen to correct the information.
                                </div>
                                <div>
                                    <Input
                                        type="textarea"
                                        onChange={this.onRejectionNotesChange}
                                        value={rejectionNotes}
                                    />

                                    {(!rejectionNotes ||
                                        rejectionNotes.length < 5) && (
                                        <Alert
                                            className="warning mt-2"
                                            fade={false}
                                        >
                                            <FontAwesomeIcon
                                                icon={faInfoCircle}
                                                className="mr-2"
                                            />
                                            Please enter some more information
                                            for the foremen.
                                        </Alert>
                                    )}
                                </div>
                            </ModalBody>
                            <ModalFooter>
                                <div className="d-flex w-100">
                                    <div className="col-6">
                                        <Button
                                            type="button"
                                            color="warning"
                                            onClick={this.toggleModal}
                                        >
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                className="mr-2"
                                            />
                                            Cancel
                                        </Button>
                                    </div>

                                    <div className="col-6">
                                        <Button
                                            type="button"
                                            color="success"
                                            onClick={onRejectClick.bind(this, {
                                                rejectionNotes,
                                            })}
                                            className="float-right"
                                            disabled={
                                                !rejectionNotes ||
                                                rejectionNotes.length < 5
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={faCloudArrowUp}
                                                className="mr-2"
                                            />
                                            Reject
                                        </Button>
                                    </div>
                                </div>
                            </ModalFooter>
                        </Modal>
                    </>
                )}
            </Can>
        );
    }
}
